import * as React from "react";

import { faHdd } from "@fortawesome/free-solid-svg-icons";
import SpeedIcon from "@mui/icons-material/Speed";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { styled } from "@mui/material/styles";
import { toast } from "react-toastify";

import { FontAwesomeIconWithTheme } from "src/common/components/ui/IconWrapper";
import useBridgeSWR from "src/common/hooks/BridgeSWRClient";
import { useDeleteWorkflow } from "src/domain/core/workflow/api/deleteWorkflow";
import { useGetScheduler } from "src/domain/core/workflow/api/getScheduler";
import { Apps } from "src/domain/core/workflow/components/active/Apps";
import { DNSRecovery } from "src/domain/core/workflow/components/active/DnsRecovery";
import { Header } from "src/domain/core/workflow/components/active/Header";
import { WorkflowMoreInfo } from "src/domain/core/workflow/components/active/MoreInfo";
import UpdateWorkflow from "src/domain/core/workflow/components/update/UpdateWorkflow";
import { endpoint } from "src/lib/bridgeClient";
import * as Tags from "src/lib/tags";
import { deleteButtonDisabled, updateButtonDisabled, appButtonsDisabled } from "src/lib/workflow";
import useStore, { DNSStatus } from "src/store/store";

const Item = styled("div")(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.primary,
}));

const NodeInfo = styled("div")(() => ({
  display: "flex",
  width: "100%",
  justifyContent: "space-between",
  margin: "5px 0 5px 0",
}));

const HeaderContainer = styled("div")(() => ({
  display: "flex",
  justifyContent: "space-evenly",
}));

const AppsContainer = styled("div")(() => ({
  display: "flex",
  justifyContent: "center",
  margin: "25px",

  a: {
    margin: "5px",
    width: "100px",
  },
}));

const GrafanaContainer = styled("div")(() => ({
  display: "flex",
  height: "100%",
  alignItems: "center",
  justifyContent: "center",
}));

interface WorkflowInfo extends Omit<Workflow, "head_node_instance_type" | "compute_instance_type"> {
  head_node_instance_type: MachineInstanceTypeDesc;
  compute_instance_type: MachineInstanceTypeDesc;
}

interface WorkflowDetailsProps {
  workflow: Workflow;
  nodes: MachineInstanceGroup;
  syncKey: string;
}

export const WorkflowHome: React.FC<WorkflowDetailsProps> = ({ workflow, nodes, syncKey }) => {
  const deleteWorkflow = useDeleteWorkflow();

  const [showInfo, setShowInfo] = React.useState(false);
  const [workflowValues, setWorkflowValues] = React.useState<Workflow>(workflow);

  const showUpdateFormForWorkflow = useStore((state) => state.showUpdateFormForWorkflow);
  const showWorkflowUpdateForm = useStore((state) => state.showWorkflowUpdateForm);
  const hideWorkflowUpdateForm = useStore((state) => state.hideWorkflowUpdateForm);
  const dnsRecordExists = useStore((state) => state.getDnsRecordStatus(workflow?.id));

  const [btnsDisabled, setBtnsDisabled] = React.useState({
    apps: false,
    update: false,
    delete: false,
  });

  const headNodes = nodes?.head;
  const computeNodes = nodes?.compute;

  const { data: disk } = useBridgeSWR<Disk>(workflow?.volume_id ? endpoint.getMyDisk(workflow?.volume_id) : null);
  const { data: applications } = useBridgeSWR<StackApplication[]>(
    workflow?.stack_id ? endpoint.appsByStackID(workflow?.stack_id) : null
  );
  const { data: scheduler } = useGetScheduler(workflowValues.scheduler_id);

  React.useEffect(() => {
    setWorkflowValues(workflow);
    setBtnsDisabled({
      // If it is confirmed that DNS record does not exist, only then disable the apps button
      apps: appButtonsDisabled(workflow?.status) || dnsRecordExists === DNSStatus.NOT_EXIST,
      update: updateButtonDisabled(workflow?.status),
      delete: deleteButtonDisabled(workflow?.status),
    });
  }, [workflow, dnsRecordExists]);

  const infoClick = async () => {
    setShowInfo(!showInfo);
  };

  const cancelClickCb = () => {
    hideWorkflowUpdateForm(workflow?.id);
  };

  const updateClickCb = () => {
    setBtnsDisabled({
      apps: true,
      update: true,
      delete: false,
    });

    hideWorkflowUpdateForm(workflow?.id);
  };

  const deleteClick = async () => {
    const workflowDisplayName = workflowValues?.tags[Tags.workflowDisplayName]
      ? workflowValues.tags[Tags.workflowDisplayName]
      : workflowValues?.name;

    const resp = await deleteWorkflow(workflowValues.id, workflowDisplayName);
    if (resp && resp.ok) {
      if (disk?.name) {
        toast("Work has been saved to disk: " + disk?.name, {
          type: "info",
        });
      }

      setBtnsDisabled({
        apps: true,
        update: true,
        delete: true,
      });
    }
  };

  if (showInfo) {
    return <WorkflowMoreInfo workflow={workflowValues} disk={disk} cb={infoClick} />;
  }

  if (showUpdateFormForWorkflow?.get(workflow?.id)) {
    const workflowInfo: WorkflowInfo = {
      ...workflowValues,
      head_node_instance_type: headNodes?.find((node) => node.instance_type === workflowValues.head_node_instance_type),
      compute_instance_type: computeNodes?.find((node) => node.instance_type === workflowValues.compute_instance_type),
    };

    return (
      <UpdateWorkflow
        currentData={workflowInfo}
        computeNodes={computeNodes}
        headNodes={headNodes}
        stackID={workflowInfo.stack_id}
        submitCb={updateClickCb}
        cancelCb={cancelClickCb}
      />
    );
  }

  const apps = applications?.filter((app) => !app.name.match(/grafana/i));
  const grafana = applications?.find((app) => app.name.match(/grafana/i));
  const hostname = workflowValues?.tags?.[Tags.dnsName];
  const workflowDataTestId = workflowValues?.tags[Tags.workflowDisplayName]
    ? workflowValues.tags[Tags.workflowDisplayName]
    : workflowValues?.name;
  return (
    <div data-testid={workflowDataTestId}>
      <HeaderContainer>
        <Header
          workflow={workflowValues}
          disk={disk}
          scheduler={scheduler}
          deleteDisabled={btnsDisabled.delete}
          updateDisabled={btnsDisabled.update}
          infoClick={infoClick}
          updateClick={async () => showWorkflowUpdateForm(workflow?.id)}
          deleteClick={deleteClick}
          syncKey={syncKey}
        />
      </HeaderContainer>
      <AppsContainer>
        <Box>
          <DNSRecovery workflow={workflowValues} />
          <Apps applications={apps} btnsDisabled={btnsDisabled.apps} hostname={hostname} />
        </Box>
      </AppsContainer>
      <Divider />
      <NodeInfo data-testid={`${workflowDataTestId}_head-node`}>
        <Item>Head Node</Item>
        <Item>{headNodes?.find((node) => node.instance_type === workflowValues.head_node_instance_type)?.name}</Item>
        <Item>
          <FontAwesomeIconWithTheme icon={faHdd} /> {disk?.size} GB
        </Item>
      </NodeInfo>
      <Divider />
      <div style={{ height: "100px" }}>
        <GrafanaContainer>
          {grafana ? (
            <Button
              variant="outlined"
              color="primary"
              disabled={!hostname || btnsDisabled.apps}
              href={"https://" + hostname + "/" + grafana?.url}
              target="_blank"
            >
              <SpeedIcon /> Performance
            </Button>
          ) : (
            <span>No Metrics Available</span>
          )}
        </GrafanaContainer>
      </div>
      <Divider />
      <NodeInfo data-testid={`${workflowDataTestId}_compute-node`}>
        <Item>Cluster Nodes</Item>
        <Item>{computeNodes?.find((node) => node.instance_type === workflowValues.compute_instance_type)?.name}</Item>
        <Item>O nodes (of {workflowValues.max_queue_size})</Item>
        <Item>O pending jobs</Item>
      </NodeInfo>
      <Divider />
    </div>
  );
};
